import React from 'react';
import InputRange from 'react-input-range';
import Divider from 'material-ui/Divider';
import Toggle from 'material-ui/Toggle';
import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import NumericInputBox from 'components/pure/utils/NumericInputBox';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';

import 'react-input-range/lib/css/index.css';
import 'styles/search/NumericSearchField.styl';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

const numericInputTypeFrom = 'from-numeric-input';
const numericInputTypeTo = 'to-numeric-input';
/** @type {number} The delta value that's used to add or subtract from the
 *  current value when the user clicks a step button in <NumericInputBox />
 */
const numericValueDelta = 0.1;

/**
 * A `NumericSearchField` renders a material-ui Checkbox for a numeric
 * search field such as "Duration" or "Opportunity Price Point"
 */
@componentHoc
class NumericSearchField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toggled: false,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (typeof nextProps.toggled !== 'undefined') {
      this.setState({
        toggled: nextProps.toggled,
      });
    }
  }

  /**
   * Callback function for the toggle of the whole numeric search widget
   * @param {object} event An event object
   * @param {boolean} toggled A boolean indicating whether the search widget is toggled or not
   */
  onToggleNumericSearchField = (event, toggled) => {
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.numericWidgetType,
      toggled,
      this.props.searchField
    );

    setTimeout(() => {
      SearchFieldsActions.updateNumericSearchExpression(this.props.searchField, toggled);
    }, ConstValues.delayDurationForSearchWidgetToggles);

    if (toggled) {
      this.props.updateLastToggledOnSearchWidget(this.props.searchField.name);
    }
  };

  /**
   * Invoked when the component's DOM representation has been updated.
   * Scrolls to the widget if it is the most recent Search Widget to be toggled on.
   */
  componentDidUpdate() {
    if (this.props.shouldFocus) {
      this.props.scrollWidgetIntoViewIfNecessary();
      // Reset the last toggled or it will keep focusing the last component on all future renders.
      this.props.updateLastToggledOnSearchWidget(null);
    }
  }

  /**
   * Triggered when the user slides the slider of <InputRange /> or clicks anywhere within the slider.
   * When this happens, we use `this.props.onInputRangeChange` which kicks off a search to the api
   * backend using the new `values` chosen by the user
   * @param values - values chosen by the user
   */
  onInputRangeValuesChange = values => {
    const floatValues = this.inputRangeValuesIntToFloat({
      min: values.min < 0 ? 0 : values.min,
      max: values.max,
    });
    this.props.onInputRangeChange(this.props.searchField, floatValues);
  };

  onChangeLowerInputNumber(value) {
    const values = {
      max: this.props.values.max,
      min: value,
    };
    this.props.onInputRangeChange(this.props.searchField, values);
  }

  onChangeHigherInputNumber(value) {
    const values = {
      min: this.props.values.min,
      max: value,
    };
    this.props.onInputRangeChange(this.props.searchField, values);
  }

  /**
   * Return {from: `from`, to: `to`} object from `values`
   * @param  {immutable/Record} searchField A search field object
   * @param  {object} values      An object that contains the min and max values
   * @return {object}             A {from: `from`, to: `to`} objecet
   */
  fromAndToValues(values) {
    return {
      from: Number(Number(values.min).toFixed(ConstValues.numberOfDigitsInNumericSearchFields)),
      to: Number(Number(values.max).toFixed(ConstValues.numberOfDigitsInNumericSearchFields)),
    };
  }

  /**
   * Touch tap a numeric input's down button to trigger a search update
   * @param  {number} value The current value
   * @param  {string} type  Type of the <NumericInputBox /> component which could be either a
   *                        `from` input or a `to` input
   */
  onTouchTapNumericInputDownButton = (value, type) => {
    const newValue = value - numericValueDelta;
    this.updateResultsWithDownValue(newValue, type);
  };

  /**
   * Change a numeric input's value to trigger a search update
   * @param  {number} value The current value
   * @param  {string} type  Type of the <NumericInputBox /> component which could be either a
   *                        `from` input or a `to` input
   */
  onValueUpdateNumericDownInput = (value, type) => {
    this.updateResultsWithDownValue(value, type);
  };

  /**
   * Update search results with a new down input `value` and a given `type`
   * @param  {number} value The current value
   * @param  {string} type  Type of the <NumericInputBox /> component which could be either a
   *                        `from` input or a `to` input
   */
  updateResultsWithDownValue(value, type) {
    if (value >= this.props.minValue) {
      let values = null;
      if (type === numericInputTypeFrom) {
        values = {
          max: this.props.values.max,
          min: value,
        };
      } else if (type === numericInputTypeTo) {
        values = {
          min: this.props.values.min,
          max: value,
        };
      }
      if (values) {
        this.props.onInputRangeChange(this.props.searchField, values);
      }
    }
  }

  /**
   * Touch tap a numeric input's up button to trigger a search update
   * @param  {number} value The current value
   * @param  {string} type  Type of the <NumericInputBox /> component which could be either a
   *                        `from` input or a `to` input
   */
  onTouchTapNumericInputUpButton = (value, type) => {
    const newValue = value + numericValueDelta;
    this.updateResultsWithUpValue(newValue, type);
  };

  /**
   * Change a numeric input's value to trigger a search update
   * @param  {number} value The current value
   * @param  {string} type  Type of the <NumericInputBox /> component which could be either a
   *                        `from` input or a `to` input
   */
  onValueUpdateNumericUpInput = (value, type) => {
    this.updateResultsWithUpValue(value, type);
  };

  /**
   * Update the search results using `value` and `type`
   * @param  {number} value The current value
   * @param  {string} type  Type of the <NumericInputBox /> component which could be either a
   *                        `from` input or a `to` input
   */
  updateResultsWithUpValue(value, type) {
    if (value <= this.props.maxValue) {
      let values = null;
      if (type === numericInputTypeFrom) {
        values = {
          max: this.props.values.max,
          min: value,
        };
      } else if (type === numericInputTypeTo) {
        values = {
          min: this.props.values.min,
          max: value,
        };
      }
      if (values) {
        this.props.onInputRangeChange(this.props.searchField, values);
      }
    }
  }

  /**
   * <react-input-range /> has a bug where it cannot handle float values properly. Therefore, this function
   * converts a float value to an integer value by multiplying the float by 100 and the remove all decimals
   * @param  {float} floatValue A float value which should be converted to an integer value
   * @return {integer}            A integer value multiplied from `floatValue` by 100
   */
  inputRangeValueFromFloatToInt(floatValue) {
    return Math.trunc(floatValue * 100);
  }

  /**
   * This is a reverse function of `convertInputRangeValueFromFloatToInt`. In this function, we divide the
   * `intValue` by 100 and fix the number of decimal places to 2
   * @param  {integer} intValue An integer value which should be converted to a float value
   * @return {float}          A float value converted from `intValue` by a divison of 100
   */
  inputRangeValueFromIntToFloat(intValue) {
    return intValue.toFixed(2) / 100.0;
  }

  /**
   * Return a {min, max} object whose `min` and `max` integer values are obtained from the argument `values`
   * that's passed into this function. We want to return integer values to <InputRange /> because react-input-range
   * has a bug where it cannot handle float input values properly
   * @param  {object} values A {min, max} object which contains the current min and max float values for
   *                         the slider in <InputRange />
   * @return {object}        A {min, max} object which contains the current min and max integer values for
   *                           the slider in <InputRange />
   */
  inputRangeValuesFloatToInt(values) {
    const {from: fromValue, to: toValue} = values;
    return {
      min: this.inputRangeValueFromFloatToInt(fromValue),
      max: this.inputRangeValueFromFloatToInt(toValue),
    };
  }

  /**
   * Return a {min, max} object whose `min` and `max` float values are obtained from the argument `values`
   * that's passed into this function. We want to return float values to the parent component because the integer
   * min and max in `values` were converted from the orignial pair of float min and max values
   * @param  {object} values A {min, max} object which contains the current min and max integer values
   * @return {object}        A {min, max} object which contains the current min and max float values
   */
  inputRangeValuesIntToFloat(values) {
    const {min: fromValue, max: toValue} = values;
    return {
      min: this.inputRangeValueFromIntToFloat(fromValue),
      max: this.inputRangeValueFromIntToFloat(toValue),
    };
  }

  renderInputRange(minValue, maxValue, fromValue, toValue) {
    const min = this.inputRangeValueFromFloatToInt(minValue);
    const max = this.inputRangeValueFromFloatToInt(maxValue);
    const inputRangeValues = this.inputRangeValuesFloatToInt({
      from: fromValue,
      to: toValue,
    });
    return (
      <InputRange
        minValue={min}
        maxValue={max}
        value={inputRangeValues}
        onChange={this.onInputRangeValuesChange}
        formatLabel={() => ''}
      />
    );
  }

  /**
   * Render a JSX component for `searchField` which should render a slider above two
   * text input fields with stepper buttons to the right
   * @param  {object} searchField A numeric search field
   * @return {JSX}             A numeric search widget rendered in JSX
   */
  renderNumericSearchWidget(searchField) {
    const minValue = this.props.minValue;
    const maxValue = this.props.maxValue;
    const {from: fromValue, to: toValue} = this.fromAndToValues(this.props.values);
    return (
      <div className="numeric-search-field">
        {this.renderInputRange(minValue, maxValue, fromValue, toValue)}
        <NumericInputBox
          className="from-input-box"
          value={fromValue}
          type={numericInputTypeFrom}
          unit={searchField.unit}
          unitPosition={searchField.unit_fix_position}
          onTouchTapDownButton={this.onTouchTapNumericInputDownButton}
          onTouchTapUpButton={this.onTouchTapNumericInputUpButton}
          onValueUpdate={this.onValueUpdateNumericUpInput}
          isNumericValueValid={isNumericValueValid}
          shouldRenderTriangles={false}
        />
        <div className="separator">
          <span>to</span>
        </div>
        <NumericInputBox
          className="to-input-box"
          value={toValue}
          type={numericInputTypeTo}
          unit={searchField.unit}
          unitPosition={searchField.unit_fix_position}
          onTouchTapDownButton={this.onTouchTapNumericInputDownButton}
          onTouchTapUpButton={this.onTouchTapNumericInputUpButton}
          onValueUpdate={this.onValueUpdateNumericDownInput}
          isNumericValueValid={isNumericValueValid}
          shouldRenderTriangles={false}
        />
      </div>
    );
  }

  render() {
    const classes = cn('numeric-search-widget', getSearchFieldClass(this.props.searchField.name));
    return (
      <div className={classes}>
        <Toggle
          label={this.props.searchField.name}
          defaultToggled={false}
          onToggle={this.onToggleNumericSearchField}
          toggled={this.state.toggled}
        />
        <Divider style={{marginTop: '5px'}} />
        {(() => {
          if (this.state.toggled) {
            return this.renderNumericSearchWidget(this.props.searchField);
          }
          return null;
        })()}
      </div>
    );
  }
}

/**
 * Return true if `value` is a valid number; false otherwise
 * @param  {number} value A new string value submitted by the user. We should check
 *                        whether its value is valid or not before passing it to the search store
 */
function isNumericValueValid(value) {
  let matched = value.match(/^-?\d*(\.?\d*)?$/);
  return Boolean(matched);
}

export default NumericSearchField;
